import React, { useEffect, useRef, useState } from "react";
import {
  Modal, Form, Input, Select, DatePicker,
  TimePicker, Checkbox, Row, Col, message
} from "antd";
import styled from "@emotion/styled";
import { CheckCircleFilled } from "@ant-design/icons";
import { connect } from "react-redux";
import { getUserList, getUserInfo } from "redux/selectors";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;

// ── Styled — copied exactly from AddTask ──────────────────────────────────────

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
    padding: 20px 24px 0;
  }
  .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
  .ant-modal-body {
    padding: 16px 24px;
  }
  .ant-modal-footer {
    border-top: none;
    padding: 0 24px 20px;
    display: flex;
    justify-content: flex-end;
    gap: 20px;
  }
  .ant-form-item-label > label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }
  .ant-input,
  .ant-picker,
  .ant-select-selector {
    border-radius: 6px !important;
    border-color: #d1d5db !important;
    font-size: 13px !important;
  }
  .ant-input:focus,
  .ant-picker-focused,
  .ant-select-focused .ant-select-selector {
    border-color: #10b981 !important;
    box-shadow: none !important;
  }
  .ant-picker { width: 100%; }
  .ant-select { width: 100%; }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #22c55e !important;
    border-color: #22c55e !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: #fff !important;
  }
`;

const CancelBtn = styled.button`
  height: 36px;
  padding: 0 24px;
  border-radius: 6px;
  border: 1px solid #000000;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  cursor: pointer;
  &:hover { border-color: #000000; }
`;

const SaveBtn = styled.button`
  height: 36px;
  padding: 0 28px;
  border-radius: 6px;
  border: none;
  background: #00685F;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff !important;
  cursor: pointer;
  &:hover { background: #00685F; }
`;

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0 16px;
  gap: 12px;
  .success-text {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
`;

// ── Options — same as AddTask ─────────────────────────────────────────────────
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];

// ── Hardcoded task status options (always available, merged with API-fetched ones)
const HARDCODED_TASK_STATUS_OPTIONS = [
  { label: "Open", value: "Open" },
  { label: "InProgress", value: "InProgress" },
  { label: "Completed", value: "Completed" },
];

// ── Component ─────────────────────────────────────────────────────────────────

function EditTask({
  isModalVisible, closeModal, getTaskList,
  taskDetails, userList, userInfo,
}) {
  const [form] = Form.useForm();
  const [showLoader, setShowLoader] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const containerRef = useRef(null);
  const [taskTypeOptions, setTaskTypeOptions] = useState([]);
  // ── Seed with hardcoded statuses so they're available immediately, before the API responds ──
  const [taskStatusOptions, setTaskStatusOptions] = useState(HARDCODED_TASK_STATUS_OPTIONS);
  const [matterList, setMatterList] = useState([]);


  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [typeRes, statusRes, matterRes] = await Promise.all([
          CALL_FEATCH_API(`insyts-task/gettasktype?userid=${userInfo?.userid}&page=1&limit=500`, "GET"),
          CALL_FEATCH_API(`insyts-task/gettaskstatus?userid=${userInfo?.userid}&page=1&limit=500`, "GET"),
          CALL_FEATCH_API(
            `insyts-matter/getmatter?userid=${userInfo?.userid}&managingoffice=${encodeURIComponent(userInfo?.managingoffice || "")}&page=1&limit=100`,
            "GET"
          ),
        ]);
        if (typeRes?.response?.data) {
          setTaskTypeOptions(typeRes.response.data.map(t => ({
            label: t.typename,
            value: t.typename,
          })));
        }
        if (statusRes?.response?.data) {
          const apiStatusOptions = statusRes.response.data.map(s => ({
            label: s.statusname,
            value: s.statusname,
          }));

          // ── Merge hardcoded statuses with API-fetched ones, de-duped by value ──
          const mergedStatusOptions = [...HARDCODED_TASK_STATUS_OPTIONS];
          apiStatusOptions.forEach((opt) => {
            if (!mergedStatusOptions.some((m) => m.value === opt.value)) {
              mergedStatusOptions.push(opt);
            }
          });
          setTaskStatusOptions(mergedStatusOptions);
        } else {
          // API failed or returned nothing — fall back to hardcoded options only
          setTaskStatusOptions(HARDCODED_TASK_STATUS_OPTIONS);
        }
        const matterData = matterRes?.response?.data || matterRes?.data || [];
        if (Array.isArray(matterData)) setMatterList(matterData);
      } catch (err) {
        console.error("[EditTask] fetchOptions error:", err);
      }
    };
    if (userInfo?.userid) fetchOptions();
  }, [userInfo?.userid]);

  const allUsers = userList || [];
  const isViewOnly = taskDetails?.isViewOnly || false;

  // ── Pre-fill form when taskDetails changes ────────────────────────────────

  useEffect(() => {
    if (isModalVisible && taskDetails?.taskid) {
      form.setFieldsValue({
        taskname: taskDetails.taskname || "",
        priority: taskDetails.priority || undefined,
        assignedto: taskDetails.assignedto || undefined,
        isprivate: taskDetails.isprivate || false,
        tasktype: taskDetails.tasktype || undefined,
        taskstatus: taskDetails.taskstatus || "Open",
        matterid: taskDetails.matterid || undefined,
        mattername: taskDetails.mattername || "",
        clientid: taskDetails.clientid || "",
        estimatedtime: taskDetails.estimatedtime || "",
        duedate: taskDetails.duedate
          ? moment(taskDetails.duedate)
          : null,
        duetime: taskDetails.duetime
          ? moment(taskDetails.duetime, "HH:mm")
          : null,
        description: taskDetails.description || "",
      });
    }
  }, [taskDetails, isModalVisible]);

  // ── Reset on close ────────────────────────────────────────────────────────

  const handleCancel = () => {
    form.resetFields();
    setShowSuccess(false);
    closeModal(false);
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const onFinish = async (values) => {
    setShowLoader(true);
    try {
      const assignedUser = allUsers.find((u) => u.userid === values.assignedto);

      const payload = {
        taskid: taskDetails.taskid,
        taskname: values.taskname,
        priority: values.priority || null,
        assignedto: values.assignedto || null,
        assignedtoname: assignedUser?.fullname || taskDetails.assignedtoname || null,
        tasktype: values.tasktype || null,
        taskstatus: values.taskstatus || "Open",
        matterid: values.matterid || null,
        mattername: values.mattername || null,
        clientid: values.clientid || null,
        duedate: values.duedate ? values.duedate.format("YYYY-MM-DD") : null,
        duetime: values.duetime ? values.duetime.format("HH:mm") : null,
        description: values.description || null,
        isprivate: values.isprivate || false,
        estimatedtime: values.estimatedtime || null,
        userid: userInfo?.userid,
        userName: userInfo?.name || null,
        updatedby: userInfo?.username || userInfo?.userid || "",
      };

      const response = await CALL_API("insyts-task/edittask", "POST", payload);

      if (response?.code === "ALREADY_EXISTS" || response?.response?.code === "ALREADY_EXISTS") {
        message.error("A task with this name already exists.");
        return;
      }

      if (response?.code === "SUCCESS") {
        form.resetFields();
        try { getTaskList(); } catch (_) { }
        closeModal(true);
      } else {
        message.error(response?.response?.message || "Failed to update task.");
      }
    } catch (err) {
      console.error("EditTask error:", err);
      message.error("Something went wrong.");
    } finally {
      setShowLoader(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <StyledModal
      title={isViewOnly ? "View Task" : "Edit Task"}
      visible={isModalVisible}
      onCancel={handleCancel}
      width={700}
      destroyOnClose
      footer={
        showSuccess ? null : (
          <>
            <CancelBtn onClick={handleCancel}>Cancel</CancelBtn>
            {!isViewOnly && (
              <SaveBtn onClick={() => form.submit()} disabled={showLoader}>
                {showLoader ? "Saving..." : "Save"}
              </SaveBtn>
            )}
          </>
        )
      }
    >
      {/* ── Success state ── */}
      {showSuccess ? (
        <SuccessWrap>
          <CheckCircleFilled style={{ fontSize: 56, color: "#10b981" }} />
          <div className="success-text">Updated</div>
        </SuccessWrap>
      ) : (
        // ── Form ── (requiredMark={true} shows red * on all required fields)
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={true}
        >
          <Row gutter={16}>
            {/* Name */}
            <Col xs={24} sm={12}>
              <Form.Item
                label="Name of the Task"
                name="taskname"
                rules={[{ required: true, message: "Please enter task name" }]}
              >
                <Input placeholder="Enter task name" disabled={isViewOnly} />
              </Form.Item>
            </Col>

            {/* Priority */}
            <Col xs={24} sm={12}>
              <Form.Item
                label="Priority"
                name="priority"
                rules={[{ required: true, message: "Please select priority" }]}
              >
                <Select placeholder="Select priority" disabled={isViewOnly}>
                  {PRIORITY_OPTIONS.map((p) => (
                    <Option key={p} value={p}>{p}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {/* Assign to */}
            <Col xs={24} sm={12}>
              <Form.Item
                label="Assign to"
                name="assignedto"
                rules={[{ required: true, message: "Please select assignee" }]}
              >
                <Select
                  showSearch
                  placeholder="Select assignee"
                  optionFilterProp="label"
                  disabled={isViewOnly}
                  getPopupContainer={() => containerRef.current}
                >
                  {allUsers.map((u) => (
                    <Option key={u.userid} value={u.userid} label={u.fullname}>
                      {u.fullname}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Task Privacy */}
            <Col xs={24} sm={12}>
              <Form.Item label="Task Privacy" name="isprivate" valuePropName="checked">
                <Checkbox disabled={isViewOnly}>Mark it Private</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {/* Task Type */}
            <Col xs={24} sm={12}>
              <Form.Item label="Task Type" name="tasktype">
                <Select placeholder="Select Type" allowClear disabled={isViewOnly}
                  getPopupContainer={() => containerRef.current}>
                  {taskTypeOptions.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Task Status */}
            <Col xs={24} sm={12}>
              <Form.Item label="Task Status" name="taskstatus" initialValue="Open">
                <Select disabled={isViewOnly}
                  getPopupContainer={() => containerRef.current}>
                  {taskStatusOptions.map((s) => (
                    <Option key={s.value} value={s.value}>{s.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {/* Matter */}
            <Col xs={24} sm={12}>
              <Form.Item label="Matter" name="matterid">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Matter"
                  optionFilterProp="label"
                  disabled={isViewOnly}
                  getPopupContainer={() => containerRef.current}
                  onChange={(value, option) => {
                    form.setFieldsValue({
                      mattername: option?.mattername || "",
                      clientid: option?.clientid || "",
                    });
                  }}
                >
                  {matterList.map((m) => (
                    <Option
                      key={m.matterId}
                      value={m.matterId}
                      label={`${m.jobTitle}`}
                      mattername={m.jobTitle}
                      clientid={m.clientId}
                    >
                      <span style={{ marginLeft: 6 }}>{m.jobTitle}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="mattername" hidden><Input /></Form.Item>
              <Form.Item name="clientid" hidden><Input /></Form.Item>
            </Col>

            {/* Estimated Time */}
            <Col xs={24} sm={12}>
              <Form.Item label="Estimated Time of Completion (optional)" name="estimatedtime">
                <Input placeholder="Enter estimated time" disabled={isViewOnly} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {/* Due Date */}
            <Col xs={24} sm={12}>
              <Form.Item
                label="Due Date"
                name="duedate"
                rules={[{ required: true, message: "Please select due date" }]}
              >
                <DatePicker
                  format="Do MMMM YYYY"
                  placeholder="Select due date"
                  disabled={isViewOnly}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>

            {/* Due Time */}
            <Col xs={24} sm={12}>
              <Form.Item label="Due Time" name="duetime">
                <TimePicker
                  use12Hours
                  format="h:mm A"
                  placeholder="Select due time"
                  disabled={isViewOnly}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Description */}
          <Form.Item label="Task Description" name="description" rules={[{ required: true, message: "Please enter task description" }]}>
            <TextArea
              placeholder="Type something..."
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={500}
              disabled={isViewOnly}
            />
          </Form.Item>

          <div ref={containerRef} />
        </Form>
      )}
    </StyledModal>
  );
}

const mapStateToProps = (state) => ({
  userList: getUserList(state),
  userInfo: getUserInfo(state),
});

export default connect(mapStateToProps, null)(EditTask);